import {
  CareEnvironment,
  carePhaseDefList,
  carePhaseDefs,
  careEnvironmentDefs,
  appliedHealthCareInflation,
  defaultHealthcareInflation,
  formatCurrency,
  hasSeparateRoomAndBoard,
  resolveUseClaimProjectionEngine,
} from '@shared';

export interface CarePlanPhaseSummary {
  label: string;
  /** Estimated age the phase begins, rounded; null when unknown. */
  ageYears: number | null;
  /** Display label for the applied care environment, or null when none. */
  environmentLabel: string | null;
  /** True when the applied environment matches the predicted recommendation. */
  isPredicted: boolean;
  durationMonths: number;
  /** Pre-formatted unit cost, e.g. "$29/hr" or "$7,023/mo"; "-" when unknown. */
  unitCostDisplay: string;
}

export interface CarePlanSummary {
  /** Hidden when the client has already started LTC (mirrors the editor). */
  showCareStartAge: boolean;
  careStartAge: number;
  inflationRatePct: number;
  defaultInflationPct: number;
  phases: CarePlanPhaseSummary[];
}

/**
 * Pure, read-only mirror of the Edit Care Assumptions screen. Derives every
 * displayed value directly from the saved `client` (no preview state, no form),
 * so it can be rendered inside a static popover without side effects.
 *
 * Mirrors the derivations in `features/edit-assumptions` (care-timeline-table,
 * care-assumptions-section, inflation-rate-section) and their hooks.
 */
export function buildCarePlanSummary(
  client: Client,
  localCosts: LocalCareEnvironmentCosts,
): CarePlanSummary {
  const {
    intakeSurvey: { clientBirthYear, clientHasStartedLtc, clientCurrentCarePhase },
    appliedInferenceSet: { ltcDurationYears },
    clientPhasePredictedStartYears,
    carePhaseDurationSelections = {},
    careEnvironmentSelections,
    careEnvironmentCosts,
    phaseCosts = [],
  } = client;

  const currentCarePhaseIndex = carePhaseDefList.findIndex(
    carePhaseDef => carePhaseDef.value === clientCurrentCarePhase,
  );
  const isPastPhase = (phaseIndex: number) =>
    phaseIndex < (currentCarePhaseIndex === -1 ? 0 : currentCarePhaseIndex);

  const predictedDuration = (phaseIndex: number) => {
    if (!phaseCosts?.[phaseIndex] || !ltcDurationYears) return 0;
    return Math.round(phaseCosts[phaseIndex].phaseDurationRatio * ltcDurationYears * 12);
  };

  // Under the claim-projection engine IL splits into home-style care
  // (policy-eligible) plus out-of-pocket room & board. The unit cost surfaces
  // the covered care portion — the home hourly care rate, tagged as such — since
  // that is what the policy pays against; the OOP room & board figure lives in
  // the Cost Summary card. Every other environment / the legacy engine keeps its
  // bare unit rate.
  const useClaimProjectionEngine = resolveUseClaimProjectionEngine(client);
  const unitCostDisplay = (env: CareEnvironment | null) => {
    if (env == null) return '-';
    const isSeparateRoomAndBoard = useClaimProjectionEngine && hasSeparateRoomAndBoard(env);
    const rateEnv = isSeparateRoomAndBoard ? CareEnvironment.home : env;
    const unit = rateEnv === CareEnvironment.home ? 'hr' : 'mo';
    const suffix = isSeparateRoomAndBoard ? ' · care services' : '';
    const customCost = careEnvironmentCosts?.[rateEnv];
    if (customCost != null) return `${formatCurrency(customCost)}/${unit}${suffix}`;
    const localRate = localCosts[rateEnv]?.rateAmount;
    if (localRate != null) return `${formatCurrency(localRate)}/${unit}${suffix}`;
    return '-';
  };

  const phases: CarePlanPhaseSummary[] = carePhaseDefList
    .filter(carePhaseDef => !isPastPhase(carePhaseDefs[carePhaseDef.value].index))
    .map(carePhaseDef => {
      const carePhase = carePhaseDef.value;
      const phaseIndex = carePhaseDefs[carePhase].index;

      const predictedStartYear = clientPhasePredictedStartYears?.[carePhase];
      const ageYears = predictedStartYear
        ? Math.round(predictedStartYear - clientBirthYear)
        : null;

      const recommendedEnv = phaseCosts?.[phaseIndex]?.recommendedCareEnvironment ?? null;
      const env =
        careEnvironmentSelections?.[carePhase] ??
        phaseCosts?.[phaseIndex]?.appliedCareEnvironment ??
        null;

      return {
        label: carePhaseDef.label,
        ageYears,
        environmentLabel: env != null ? careEnvironmentDefs[env].label : null,
        isPredicted: env != null && env === recommendedEnv,
        durationMonths:
          carePhaseDurationSelections[carePhase] ?? predictedDuration(phaseIndex),
        unitCostDisplay: unitCostDisplay(env),
      };
    });

  return {
    showCareStartAge: !clientHasStartedLtc,
    careStartAge: client.clientCustomInferences.ltcAtAge ?? client.inferenceSet?.ltcAtAge ?? 65,
    inflationRatePct: parseFloat((appliedHealthCareInflation(client) * 100).toFixed(1)),
    defaultInflationPct: defaultHealthcareInflation(client, true),
    phases,
  };
}
